import type { WeeklyOperatingHours, SpecialDateOperatingHours, Store } from '@geely/shared/types';

/**
 * Generates time slots for a specific time range, excluding lunch break if configured
 */
export function generateTimeSlotsWithLunchBreak(
  startTime: string,
  endTime: string,
  timeInterval: number,
  lunchBreak?: { start: string; end: string }
): string[] {
  const slots: string[] = [];
  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);

  const startMinutes = startHour * 60 + startMin;
  const endMinutes = endHour * 60 + endMin;

  let lunchStartMinutes: number | null = null;
  let lunchEndMinutes: number | null = null;

  if (lunchBreak) {
    const [lunchStartHour, lunchStartMin] = lunchBreak.start.split(':').map(Number);
    const [lunchEndHour, lunchEndMin] = lunchBreak.end.split(':').map(Number);
    lunchStartMinutes = lunchStartHour * 60 + lunchStartMin;
    lunchEndMinutes = lunchEndHour * 60 + lunchEndMin;
  }

  for (let minutes = startMinutes; minutes < endMinutes; minutes += timeInterval) {
    const slotStart = minutes;
    const slotEnd = minutes + timeInterval;

    // Check if slot overlaps with lunch break
    if (lunchStartMinutes !== null && lunchEndMinutes !== null) {
      const overlapsLunch = (
        (slotStart >= lunchStartMinutes && slotStart < lunchEndMinutes) ||
        (slotEnd > lunchStartMinutes && slotEnd <= lunchEndMinutes) ||
        (slotStart <= lunchStartMinutes && slotEnd >= lunchEndMinutes)
      );

      if (overlapsLunch) {
        continue; // Skip this slot
      }
    }

    const hour = Math.floor(minutes / 60);
    const min = minutes % 60;
    slots.push(`${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`);
  }

  return slots;
}

/**
 * Generates available dates for the next N days based on cascade logic:
 * 1. Special date override (if exists)
 * 2. Weekly pattern (if exists)
 * 3. Closed (no configuration)
 */
export function generateAvailableDates(
  storeId: string,
  weeklyHours: WeeklyOperatingHours[],
  specialDateHours: SpecialDateOperatingHours[],
  maxDays: number = 60
): string[] {
  const today = new Date();
  const availableDates: string[] = [];

  for (let i = 0; i < maxDays; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    const dateStr = date.toISOString().split('T')[0];
    const dayOfWeek = date.getDay();

    // CAMADA 1: Check special date override
    const specialDate = specialDateHours.find(
      (h) => h.storeId === storeId && h.date === dateStr && h.isActive
    );

    if (specialDate) {
      // If special date exists and is open, include it
      if (!specialDate.isClosed) {
        availableDates.push(dateStr);
      }
      continue; // Special date takes precedence
    }

    // CAMADA 2: Fall back to weekly pattern
    const weeklyPattern = weeklyHours.find(
      (h) => h.storeId === storeId && h.dayOfWeek === dayOfWeek && h.isActive
    );

    if (weeklyPattern) {
      // If weekly pattern exists and is open, include it
      if (!weeklyPattern.isClosed) {
        availableDates.push(dateStr);
      }
      continue;
    }

    // CAMADA 3: No configuration = closed (don't include)
  }

  return availableDates;
}

/**
 * Gets time slots for a specific date using cascade logic
 */
export function getTimeSlotsForDate(
  date: string,
  storeId: string,
  weeklyHours: WeeklyOperatingHours[],
  specialDateHours: SpecialDateOperatingHours[],
  store: Store | null
): string[] {
  const dateObj = new Date(date + 'T00:00:00');
  const dayOfWeek = dateObj.getDay();

  // CAMADA 1: Check special date override
  const specialDate = specialDateHours.find(
    (h) => h.storeId === storeId && h.date === date && h.isActive
  );

  if (specialDate) {
    if (specialDate.isClosed || !specialDate.startTime || !specialDate.endTime || !specialDate.timeInterval) {
      return [];
    }

    return generateTimeSlotsWithLunchBreak(
      specialDate.startTime,
      specialDate.endTime,
      specialDate.timeInterval,
      specialDate.hasLunchBreak && specialDate.lunchStartTime && specialDate.lunchEndTime
        ? { start: specialDate.lunchStartTime, end: specialDate.lunchEndTime }
        : undefined
    );
  }

  // CAMADA 2: Fall back to weekly pattern
  const weeklyPattern = weeklyHours.find(
    (h) => h.storeId === storeId && h.dayOfWeek === dayOfWeek && h.isActive
  );

  if (weeklyPattern) {
    if (weeklyPattern.isClosed || !weeklyPattern.startTime || !weeklyPattern.endTime || !weeklyPattern.timeInterval) {
      return [];
    }

    return generateTimeSlotsWithLunchBreak(
      weeklyPattern.startTime,
      weeklyPattern.endTime,
      weeklyPattern.timeInterval,
      weeklyPattern.hasLunchBreak && weeklyPattern.lunchStartTime && weeklyPattern.lunchEndTime
        ? { start: weeklyPattern.lunchStartTime, end: weeklyPattern.lunchEndTime }
        : undefined
    );
  }

  // CAMADA 3: No configuration = closed
  return [];
}

/**
 * Gets the max parallel appointments for a specific date using cascade logic:
 * 1. SpecialDateOperatingHours (if exists and has parallelAppointments)
 * 2. WeeklyOperatingHours (if exists and has parallelAppointments)
 * 3. Store default (fallback)
 */
export function getParallelAppointmentsForDate(
  date: string,
  storeId: string,
  weeklyHours: WeeklyOperatingHours[],
  specialDateHours: SpecialDateOperatingHours[],
  storeDefault: number
): number {
  const dateObj = new Date(date + 'T00:00:00');
  const dayOfWeek = dateObj.getDay();

  // CAMADA 1: Check special date override
  const specialDate = specialDateHours.find(
    (h) => h.storeId === storeId && h.date === date && h.isActive
  );

  if (specialDate?.parallelAppointments) {
    return specialDate.parallelAppointments;
  }

  // CAMADA 2: Fall back to weekly pattern
  const weeklyPattern = weeklyHours.find(
    (h) => h.storeId === storeId && h.dayOfWeek === dayOfWeek && h.isActive
  );

  if (weeklyPattern?.parallelAppointments) {
    return weeklyPattern.parallelAppointments;
  }

  // CAMADA 3: Store default
  return storeDefault;
}
